/**
 * Team, Onboarding: one run, the admin's side (plan docs/plans/lms-plan.md
 * 12.3, 12.4, 18.24; WP-47; R1, R14).
 *
 * GET /admin/onboarding/runs/:id answers the run with its items, progress,
 * the new team member, who started it, the site and the template. 12.3: every
 * item with the same controls, the items assigned to admins first, a verify
 * control on items that require verification, notes, and the progress bar.
 *
 * The rules the controls follow (18.24, services/lms/onboarding/items.js):
 *   - Mark done: an admin's own open items only (a new hire's item is theirs:
 *     403 LMS_ONBOARDING_HIRE_ITEM), never a course item (409 LMS_COURSE_ITEM);
 *     the evidence the item asks for, as on the hire's tab.
 *   - Not applicable: any open item, with a reason; for a course item the
 *     course assignment this run created is cancelled with it.
 *   - Verify: a done item that requires verification, once, and never by the
 *     new team member themselves (403 LMS_ONBOARDING_SELF_VERIFY).
 *   - Note: set or clear on any item while the run is active (a not
 *     applicable item's note is its reason).
 *   - Cancel: the whole run, with a reason; its own open course assignments
 *     are cancelled first.
 * Each act answers the item and the run's status; the page updates in place
 * and says when the run completes (12.4).
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Check, CheckCircle2, CircleSlash, Loader2, ShieldCheck, StickyNote } from 'lucide-react';
import { useToast } from '../../../../ui/Toast';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useLms } from '../../../LmsProvider';
import { useLmsMe } from '../../../hooks/useLmsMe';
import {
  cancelOnboardingRun,
  getAdminEvidenceUrl,
  getOnboardingRun,
  markAdminItemDone,
  markAdminItemNotApplicable,
  setOnboardingItemNote,
  verifyOnboardingItem,
} from '../../../api/onboarding';
import type { AdminOnboardingRunView, OnboardingDoneInput, OnboardingRunItem } from '../../../api/onboarding';
import { ErrorNote, metaCls } from '../../../components/pickers/shared';
import { ItemCard } from '../../onboarding/ItemCard';
import { DoneDialog } from '../../onboarding/DoneDialog';
import { ReasonDialog } from '../../onboarding/ReasonDialog';
import { useEvidenceViewer } from '../../onboarding/useEvidenceViewer';
import {
  applyItemResult,
  describeOnboardingError,
  formatDay,
  localToday,
  personLabel,
  ProgressBlock,
  RunStatusPill,
} from '../../onboarding/shared';
import { RUNS_BASE } from './paths';

const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

/** The route element: one RunPage per run id, so a new id starts clean. */
export function RunPageRoute() {
  const { runId } = useParams();
  if (!runId) return null;
  return <RunPage key={runId} runId={runId} />;
}

export function RunPage({ runId }: { runId: string }) {
  const { toast } = useToast();
  const { siteId } = useLms();
  const { me } = useLmsMe();
  const [view, setView] = useState<AdminOnboardingRunView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [doneTarget, setDoneTarget] = useState<OnboardingRunItem | null>(null);
  const [naTarget, setNaTarget] = useState<OnboardingRunItem | null>(null);
  const [noteTarget, setNoteTarget] = useState<OnboardingRunItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const evidence = useEvidenceViewer();
  const today = localToday();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOnboardingRun(runId)
      .then((data) => {
        if (!cancelled) setView(data);
      })
      .catch((err) => {
        if (!cancelled) setError(describeOnboardingError(err, 'This onboarding run could not be loaded.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, siteId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const apply = (result: Parameters<typeof applyItemResult>[1]) => {
    setView((prev) => (prev ? applyItemResult(prev, result, today) : prev));
  };

  const completedWords = (runStatus: string | undefined) =>
    runStatus === 'completed' && view?.run.status !== 'completed' ? ' The onboarding run is complete.' : '';

  const markDone = async (input: OnboardingDoneInput) => {
    if (!doneTarget) return;
    const result = await markAdminItemDone(runId, doneTarget.id, input);
    const tail = completedWords(result.run_status);
    apply(result);
    setDoneTarget(null);
    toast(`Marked "${result.title}" done.${tail}`, { type: 'success' });
  };

  const markNotApplicable = async (reason: string) => {
    if (!naTarget) return;
    const result = await markAdminItemNotApplicable(runId, naTarget.id, reason);
    const tail = completedWords(result.run_status);
    apply(result);
    setNaTarget(null);
    const cancelled =
      typeof result.assignments_cancelled === 'number' && result.assignments_cancelled > 0
        ? ` ${result.assignments_cancelled} course ${result.assignments_cancelled === 1 ? 'assignment' : 'assignments'} cancelled.`
        : '';
    toast(`Marked "${result.title}" not applicable.${cancelled}${tail}`, { type: 'success' });
  };

  const saveNote = async (text: string) => {
    if (!noteTarget) return;
    const result = await setOnboardingItemNote(runId, noteTarget.id, text ? text : null);
    apply(result);
    setNoteTarget(null);
    toast(text ? 'Note saved.' : 'Note cleared.', { type: 'success' });
  };

  const verify = async (item: OnboardingRunItem) => {
    if (verifying) return;
    setVerifying(item.id);
    try {
      const result = await verifyOnboardingItem(runId, item.id);
      const tail = completedWords(result.run_status);
      apply(result);
      toast(`Verified "${result.title}".${tail}`, { type: 'success' });
    } catch (err) {
      toast(describeOnboardingError(err, 'The item could not be verified.'), { type: 'error' });
    } finally {
      setVerifying(null);
    }
  };

  const cancelRun = async (reason: string) => {
    const result = await cancelOnboardingRun(runId, reason);
    setView((prev) => (prev ? { ...prev, run: result.run } : prev));
    setCancelling(false);
    const n = result.assignments_cancelled ?? 0;
    toast(
      n > 0
        ? `Onboarding cancelled. ${n} course ${n === 1 ? 'assignment' : 'assignments'} cancelled with it.`
        : 'Onboarding cancelled.',
      { type: 'success' }
    );
  };

  const back = (
    <Link
      to={RUNS_BASE}
      className="inline-flex min-h-[44px] items-center gap-1 rounded text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      All onboarding runs
    </Link>
  );

  if (!view) {
    return (
      <div className="space-y-4" data-testid="onboarding-run-page">
        {back}
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the onboarding run
          </p>
        ) : (
          <ErrorNote testId="onboarding-run-error">
            {error ?? 'This onboarding run could not be loaded.'}{' '}
            <button type="button" className="font-medium underline" onClick={reload}>
              Retry
            </button>
          </ErrorNote>
        )}
      </div>
    );
  }

  const run = view.run;
  const active = run.status === 'active';
  const hireName = personLabel(view.user, 'The new team member');
  const isSelf = Boolean(me?.user.id && me.user.id === run.user_id);
  const adminItems = view.items.filter((i) => i.responsible === 'admin').sort((a, b) => a.ordinal - b.ordinal);
  const hireItems = view.items.filter((i) => i.responsible !== 'admin').sort((a, b) => a.ordinal - b.ordinal);

  const actionsFor = (item: OnboardingRunItem) => {
    if (!active) return null;
    const buttons: ReactElement[] = [];
    if (item.status === 'pending' && item.responsible === 'admin' && item.kind !== 'course') {
      buttons.push(
        <button key="done" type="button" className={primaryBtnCls} onClick={() => setDoneTarget(item)} data-testid={`onboarding-admin-done-${item.id}`}>
          <Check className="h-4 w-4" aria-hidden="true" />
          Mark done
        </button>
      );
    }
    if (item.status === 'done' && item.requires_verification && !item.verified_at) {
      buttons.push(
        <button
          key="verify"
          type="button"
          className={primaryBtnCls}
          onClick={() => verify(item)}
          disabled={isSelf || verifying !== null}
          title={isSelf ? 'Another admin verifies your own onboarding items.' : undefined}
          data-testid={`onboarding-verify-${item.id}`}
        >
          {verifying === item.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4" aria-hidden="true" />}
          Verify
        </button>
      );
    }
    if (item.status === 'pending') {
      buttons.push(
        <button key="na" type="button" className={secondaryBtnCls} onClick={() => setNaTarget(item)} data-testid={`onboarding-admin-na-${item.id}`}>
          <CircleSlash className="h-4 w-4" aria-hidden="true" />
          Not applicable
        </button>
      );
    }
    buttons.push(
      <button key="note" type="button" className={secondaryBtnCls} onClick={() => setNoteTarget(item)} data-testid={`onboarding-note-${item.id}`}>
        <StickyNote className="h-4 w-4" aria-hidden="true" />
        {item.status === 'not_applicable' ? 'Edit reason' : item.note ? 'Edit note' : 'Add note'}
      </button>
    );
    return <>{buttons}</>;
  };

  const renderList = (items: OnboardingRunItem[]) => (
    <ul className="space-y-2">
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          today={today}
          viewer="admin"
          hireName={hireName}
          actions={actionsFor(item)}
          onViewEvidence={() => evidence.view(item.id, () => getAdminEvidenceUrl(runId, item.id, 'inline'))}
          viewing={evidence.opening === item.id}
          blockedUrl={evidence.blocked?.id === item.id ? evidence.blocked.url : null}
        />
      ))}
    </ul>
  );

  return (
    <div className="space-y-4" data-testid="onboarding-run-page">
      {back}

      <section className={cardCls} aria-labelledby="lms-onboarding-run-title" data-testid="onboarding-run-summary">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id="lms-onboarding-run-title"
              className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100"
            >
              <span data-testid="onboarding-run-person">{hireName}</span>
              <RunStatusPill status={run.status} testId="onboarding-run-status" />
            </h2>
            <p className={`${metaCls} mt-1`}>
              {[
                view.template?.name ?? null,
                view.site?.name ?? null,
                `First day ${formatDay(run.start_date)}`,
                `Started by ${personLabel(view.started_by, 'an admin')}`,
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            </p>
            {run.status === 'cancelled' && run.cancel_reason ? (
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{`Cancelled ${formatDay(run.cancelled_at)}: ${run.cancel_reason}`}</p>
            ) : null}
          </div>
          {active ? (
            <button type="button" className={secondaryBtnCls} onClick={() => setCancelling(true)} data-testid="onboarding-run-cancel">
              <Ban className="h-4 w-4" aria-hidden="true" />
              Cancel onboarding
            </button>
          ) : null}
        </div>
        <div className="mt-3">
          <ProgressBlock progress={view.progress} testId="onboarding-run-progress" />
        </div>
        {run.status === 'completed' ? (
          <p
            className="mt-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
            role="status"
            data-testid="onboarding-run-complete"
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Every item is done or not applicable, and every item that needs it is verified.
          </p>
        ) : null}
        {isSelf && active ? (
          <p className={`${metaCls} mt-3`}>This is your own onboarding: another admin verifies your items.</p>
        ) : null}
      </section>

      <section aria-labelledby="lms-onboarding-admin-items" data-testid="onboarding-admin-items">
        <h2 id="lms-onboarding-admin-items" className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {`For admins (${adminItems.length})`}
        </h2>
        {adminItems.length > 0 ? renderList(adminItems) : <p className={metaCls}>{"No item on this checklist is an admin's."}</p>}
      </section>

      <section aria-labelledby="lms-onboarding-hire-items" data-testid="onboarding-hire-items">
        <h2 id="lms-onboarding-hire-items" className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {`For ${hireName} (${hireItems.length})`}
        </h2>
        {hireItems.length > 0 ? renderList(hireItems) : <p className={metaCls}>{"No item on this checklist is the new team member's."}</p>}
      </section>

      <DoneDialog item={doneTarget} onClose={() => setDoneTarget(null)} onSubmit={markDone} />
      <ReasonDialog
        isOpen={naTarget !== null}
        title="Not applicable"
        intro={naTarget ? <p className="font-medium text-gray-900 dark:text-gray-100">{naTarget.title}</p> : null}
        label="Reason"
        hint={
          naTarget?.kind === 'course'
            ? 'The course assignment this onboarding created is cancelled with the item.'
            : 'Why this item does not apply to this person.'
        }
        confirmLabel="Mark not applicable"
        onClose={() => setNaTarget(null)}
        onSubmit={markNotApplicable}
        testId="onboarding-admin-na"
      />
      <ReasonDialog
        isOpen={noteTarget !== null}
        title={noteTarget?.status === 'not_applicable' ? 'Reason' : 'Note'}
        intro={noteTarget ? <p className="font-medium text-gray-900 dark:text-gray-100">{noteTarget.title}</p> : null}
        label={noteTarget?.status === 'not_applicable' ? 'Reason' : 'Note'}
        hint="Visible to the admins and to the new team member. Save it empty to clear it. Never a password or a login."
        confirmLabel="Save"
        optional
        initialValue={noteTarget?.note ?? ''}
        onClose={() => setNoteTarget(null)}
        onSubmit={saveNote}
        testId="onboarding-note"
      />
      <ReasonDialog
        isOpen={cancelling}
        title="Cancel onboarding"
        intro={`The checklist for ${hireName} stops here. The course assignments it created and nobody has finished are cancelled too.`}
        label="Reason"
        confirmLabel="Cancel onboarding"
        cancelLabel="Keep it running"
        danger
        onClose={() => setCancelling(false)}
        onSubmit={cancelRun}
        testId="onboarding-cancel"
      />
    </div>
  );
}

export default RunPage;
